import { useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { Box, Link, Stack, Tab, Tabs, TextField, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { caricaImpianto } from '../anagrafiche/impianto.api'
import { DocumentiImpianto } from '../anagrafiche/DocumentiImpianto'
import { GestionePod } from '../anagrafiche/GestionePod'
import { GestioneContatori } from '../anagrafiche/GestioneContatori'
import { ImportaXml } from '../anagrafiche/ImportaXml'
import { DatiLicenza } from './DatiLicenza'
import { QuadroImpianto } from './QuadroImpianto'
import { ETICHETTA_TIPOLOGIA } from '../home/ripartizione'
import { GrigliaLetture } from '../letture/GrigliaLetture'
import { SchedaVendite } from '../vendite/SchedaVendite'
import { ANNO_IMPOSTA } from '../lib/anno-imposta'
import { apriDichiarazioni } from '../dichiarazione/dichiarazione.api'
import { ETICHETTA_PERIODO, ETICHETTA_STATO_DICHIARAZIONE } from './stato-dichiarazione'
import type { Periodo } from '@praticheenergia/domain'

type Scheda = 'documenti' | 'contatori' | 'pod' | 'licenza' | 'letture' | 'vendite'

export function DettaglioImpianto() {
  const { idImpianto } = useParams()
  const impiantoId = Number(idImpianto)
  const [scheda, setScheda] = useState<Scheda>('documenti')
  const [periodoScelto, setPeriodoScelto] = useState<Periodo | null>(null)
  const {
    data: impianto,
    isLoading,
    error,
  } = useQuery({ queryKey: ['impianto', impiantoId], queryFn: () => caricaImpianto(impiantoId) })
  // Apre i periodi mancanti a ogni visita: idempotente, e lo stato arriva gia'
  // ricalcolato dal database.
  const { data: dichiarazioni = [] } = useQuery({
    queryKey: ['dichiarazioni', impiantoId, ANNO_IMPOSTA],
    queryFn: () => apriDichiarazioni(impiantoId, ANNO_IMPOSTA),
  })
  const dichiarazioneScelta = dichiarazioni.find((d) => d.periodo === periodoScelto) ?? dichiarazioni[0]

  if (isLoading) return null
  if (error) return <p role="alert">{error instanceof Error ? error.message : 'Errore nel caricamento dell impianto'}</p>
  if (!impianto) return null

  return (
    <Box>
      <Typography variant="h6">{impianto.ragioneSociale}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {impianto.codiceImpianto ?? impianto.codiceDitta} — {ETICHETTA_TIPOLOGIA[impianto.tipologia]}
        {impianto.potenzaNominale !== null && ` · ${impianto.potenzaNominale} kW`}
      </Typography>
      <QuadroImpianto impiantoId={impianto.id} dichiarazioni={dichiarazioni} />
      <Typography variant="h6" component="h2" sx={{ mt: 3, mb: 1 }}>
        Dichiarazioni, anagrafica e documenti
      </Typography>
      <Stack sx={{ mb: 2 }} spacing={0.5}>
        {dichiarazioni.map((d) => (
          <Link key={d.id} component={RouterLink} to={`dichiarazione/${ANNO_IMPOSTA}/${d.periodo}`}>
            Dichiarazione {ETICHETTA_PERIODO[d.periodo]} {ANNO_IMPOSTA} — {ETICHETTA_STATO_DICHIARAZIONE[d.stato]}
          </Link>
        ))}
      </Stack>
      <Tabs value={scheda} onChange={(_, v: Scheda) => setScheda(v)} sx={{ mb: 2 }}>
        <Tab label="Documenti" value="documenti" />
        <Tab label="Contatori" value="contatori" />
        <Tab label="POD" value="pod" />
        <Tab label="Dati Licenza" value="licenza" />
        <Tab label="Letture" value="letture" />
        {impianto.tipologia === 'impianto_vendita' && <Tab label="Vendite" value="vendite" />}
      </Tabs>
      {scheda === 'documenti' && <DocumentiImpianto impiantoId={impianto.id} tipologia={impianto.tipologia} />}
      {scheda === 'contatori' && (
        <>
          <ImportaXml impiantoId={impianto.id} codiceDitta={impianto.codiceDitta} />
          <GestioneContatori impiantoId={impianto.id} />
        </>
      )}
      {scheda === 'pod' && <GestionePod impiantoId={impianto.id} />}
      {scheda === 'licenza' && <DatiLicenza impianto={impianto} />}
      {(scheda === 'letture' || scheda === 'vendite') &&
        (dichiarazioneScelta === undefined ? (
          <p>Nessun periodo da dichiarare per questa tipologia</p>
        ) : (
          <>
            {dichiarazioni.length > 1 && (
              <TextField
                select
                size="small"
                label="Periodo"
                slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                value={dichiarazioneScelta.periodo}
                onChange={(e) => setPeriodoScelto(e.target.value as Periodo)}
                sx={{ mb: 2 }}
              >
                {dichiarazioni.map((d) => (
                  <option key={d.periodo} value={d.periodo}>
                    {ETICHETTA_PERIODO[d.periodo]}
                  </option>
                ))}
              </TextField>
            )}
            {scheda === 'letture' ? (
              <GrigliaLetture impiantoId={impianto.id} anno={ANNO_IMPOSTA} periodo={dichiarazioneScelta.periodo} />
            ) : (
              <SchedaVendite
                dichiarazioneId={dichiarazioneScelta.id}
                anno={ANNO_IMPOSTA}
                periodo={dichiarazioneScelta.periodo}
                modificabile={['letture_non_inserite', 'bozza'].includes(dichiarazioneScelta.stato)}
              />
            )}
          </>
        ))}
    </Box>
  )
}
